import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Tab,
  Nav,
  Button,
  Spinner,
  Alert,
  Badge,
  ListGroup,
  Image,
  Accordion,
  Table,
  ProgressBar,
  Modal,
} from "react-bootstrap";
import axios from "axios";
import { FaWrench, FaCar, FaEdit } from "react-icons/fa";
import {
  ArrowLeft,
  Calendar,
  User,
  Phone,
  Mail,
  MapPin,
  FileText,
  Settings,
  Shield,
  Battery,
  Tool,
  Camera,
  Video,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronRight,
  Clock,
  DollarSign,
  Tag,
  BarChart,
  TrendingUp,
  Layers,
  Package,
  Server,
  HardDrive,
  Cpu,
  Thermometer,
  Zap,
  BatteryCharging,
  RotateCcw,
  Wind,
  Droplets,
  Volume2,
  Smartphone,
  Radio,
  Monitor,
  Speaker,
  Bluetooth,
  Navigation,
  Wifi,
  Sun,
  Moon,
  Cloud,
  Umbrella,
  Anchor,
  Feather,
  Flag,
  Globe,
  Heart,
  Home,
  Key,
  Lock,
  Unlock,
  Map,
  Maximize2,
  Minimize2,
  MoreHorizontal,
  PieChart,
  Printer,
  RefreshCw,
  Save,
  Search,
  Share2,
  Star,
  ThumbsUp,
  Trash2,
  Truck,
  Users,
  Watch,
  WifiOff,
  X,
  ZoomIn,
  ZoomOut,
} from "react-feather";
import "./CaseDetails.css";
import { Baseurl } from "../../baseurl";
import { toast } from "react-toastify";
import { handleDeleteCase } from "../../apiServices/CaseApiServices";

const CaseDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [caseDetails, setCaseDetails] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");

  // Status mapping
  const statusConfig = {
    1: { label: "Case ID Created", variant: "primary", color: "#0d6efd" },
    2: { label: "Assigned", variant: "warning", color: "#ffc107" },
    3: { label: "In Progress", variant: "info", color: "#0dcaf0" },
    4: { label: "Completed", variant: "success", color: "#198754" },
    5: { label: "Approved", variant: "success", color: "#198754" },
  };

  // Vehicle type mapping
  const vehicleTypeLabels = {
    evTwoWheeler: "EV Two Wheeler",
    evThreeWheeler: "EV Three Wheeler",
    evFourWheeler: "EV Four Wheeler",
    evCommercial: "EV Commercial",
    evBus: "EV Bus",
  };

  useEffect(() => {
    fetchCaseDetails();
  }, [id]);

  const fetchCaseDetails = async () => {
    setLoading(true);
    setError("");
    try {
      const url = `${Baseurl}/api/cases/individualCaseDetails/${id}`;
      console.log(url, "url");
      const response = await axios.get(url);

      if (response.data.success) {
        setCaseDetails(response.data.caseDetails);
      } else {
        setError(response.data.message || "Failed to fetch case details");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch case details");
      console.error("Error fetching case details:", err);
    } finally {
      setLoading(false);
    }
  };
  const deleteCase = async () => {
    const deleteCase = await handleDeleteCase(id);
    fetchCaseDetails();
  };
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = () => {
    const status = caseDetails?.status;
    const config = statusConfig[status] || {
      label: "Unknown",
      variant: "secondary",
      color: "#6c757d",
    };

    return (
      <Badge bg={config.variant} className="px-3 py-2">
        {config.label}
      </Badge>
    );
  };

  const renderOverview = () => {
    if (!caseDetails) return null;

    return (
      <Row>
        {/* Basic Information */}
        <Col lg={4}>
          <Card className="mb-4">
            <Card.Header>
              <h6 className="mb-0">Basic Information</h6>
            </Card.Header>
            <Card.Body>
              <ListGroup variant="flush">
                <ListGroup.Item className="d-flex justify-content-between align-items-center">
                  <span className="text-muted">Case ID</span>
                  <span className="fw-bold">{caseDetails.caseID}</span>
                </ListGroup.Item>
                <ListGroup.Item className="d-flex justify-content-between align-items-center">
                  <span className="text-muted">Case Type</span>
                  <span>{caseDetails.caseType || "N/A"}</span>
                </ListGroup.Item>
                <ListGroup.Item className="d-flex justify-content-between align-items-center">
                  <span className="text-muted">Vehicle Type</span>
                  <span>
                    {vehicleTypeLabels[caseDetails.vehicleType] ||
                      caseDetails.vehicleType}
                  </span>
                </ListGroup.Item>
                <ListGroup.Item className="d-flex justify-content-between align-items-center">
                  <span className="text-muted">Valuation Purpose</span>
                  <span>{caseDetails.valuationPurpose || "N/A"}</span>
                </ListGroup.Item>
                <ListGroup.Item className="d-flex justify-content-between align-items-center">
                  <span className="text-muted">Status</span>
                  {getStatusBadge()}
                </ListGroup.Item>
                <ListGroup.Item className="d-flex justify-content-between align-items-center">
                  <span className="text-muted">Created</span>
                  <span>{formatDate(caseDetails.createdAt)}</span>
                </ListGroup.Item>
              </ListGroup>
            </Card.Body>
          </Card>

          {/* Requester Information */}
          <Card className="mb-4">
            <Card.Header>
              <h6 className="mb-0">Requester Information</h6>
            </Card.Header>
            <Card.Body>
              <div className="d-flex align-items-start mb-3">
                <div className="icon-shape icon-md bg-light-primary text-primary rounded-2 me-3">
                  <User size={20} />
                </div>
                <div>
                  <h6 className="mb-1">{caseDetails.requesterName || "N/A"}</h6>
                  <p className="text-muted mb-1">
                    <Phone size={14} className="me-1" />
                    {caseDetails.requesterPhone || "N/A"}
                  </p>
                  <p className="text-muted mb-0">
                    <MapPin size={14} className="me-1" />
                    {caseDetails.requesterBranch || "N/A"}
                  </p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Borrower Information */}
        <Col lg={4}>
          <Card className="mb-4">
            <Card.Header>
              <h6 className="mb-0">Borrower Information</h6>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <h6 className="mb-1">{caseDetails.borrowerName || "N/A"}</h6>
                <div className="text-muted">
                  <Phone size={14} className="me-1" />
                  {caseDetails.borrowerPhoneNo || "N/A"}
                </div>
                {caseDetails.borrowerEmail && (
                  <div className="text-muted">
                    <Mail size={14} className="me-1" />
                    {caseDetails.borrowerEmail}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <h6 className="mb-2">Address</h6>
                <p className="text-muted mb-1">
                  {caseDetails.borrowerAddress || "N/A"}
                </p>
                <div className="text-muted">
                  {caseDetails.borrowerCity && `${caseDetails.borrowerCity}, `}
                  {caseDetails.borrowerState}
                </div>
              </div>

              {caseDetails.borrowerCoordinates && (
                <div className="mb-3">
                  <h6 className="mb-2">Coordinates</h6>
                  <div className="text-muted">
                    <span className="me-3">
                      Lat: {caseDetails.borrowerCoordinates.lat || "N/A"}
                    </span>
                    <span>
                      Lng: {caseDetails.borrowerCoordinates.lng || "N/A"}
                    </span>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Vehicle Details */}
          <Card className="mb-4">
            <Card.Header>
              <h6 className="mb-0">Vehicle Details</h6>
            </Card.Header>
            <Card.Body>
              <ListGroup variant="flush">
                <ListGroup.Item className="d-flex justify-content-between align-items-center">
                  <span className="text-muted">Brand</span>
                  <span>{caseDetails.brand || "N/A"}</span>
                </ListGroup.Item>
                <ListGroup.Item className="d-flex justify-content-between align-items-center">
                  <span className="text-muted">Model</span>
                  <span>{caseDetails.model || "N/A"}</span>
                </ListGroup.Item>
                <ListGroup.Item className="d-flex justify-content-between align-items-center">
                  <span className="text-muted">Variant</span>
                  <span>{caseDetails.variant || "N/A"}</span>
                </ListGroup.Item>
                <ListGroup.Item className="d-flex justify-content-between align-items-center">
                  <span className="text-muted">Registration No</span>
                  <span>
                    {caseDetails.regdNo ||
                      caseDetails.vehicleEdetails?.regNo ||
                      "N/A"}
                  </span>
                </ListGroup.Item>
                <ListGroup.Item className="d-flex justify-content-between align-items-center">
                  <span className="text-muted">Chassis No</span>
                  <span>
                    {caseDetails.chasisNo ||
                      caseDetails.vehicleEdetails?.chassis ||
                      "N/A"}
                  </span>
                </ListGroup.Item>
                <ListGroup.Item className="d-flex justify-content-between align-items-center">
                  <span className="text-muted">Engine No</span>
                  <span>
                    {caseDetails.engNo ||
                      caseDetails.vehicleEdetails?.engine ||
                      "N/A"}
                  </span>
                </ListGroup.Item>
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>

        {/* Status & Assignment */}
        <Col lg={4}>
          <Card className="mb-4">
            <Card.Header>
              <h6 className="mb-0">Assignment Details</h6>
            </Card.Header>
            <Card.Body>
              {caseDetails.assignedAgent?.id ? (
                <>
                  <div className="d-flex align-items-start mb-3">
                    <div className="icon-shape icon-md bg-light-info text-info rounded-2 me-3">
                      <User size={20} />
                    </div>
                    <div>
                      <h6 className="mb-1">
                        {caseDetails.assignedAgent.id.name || "N/A"}
                      </h6>
                      <p className="text-muted mb-1">
                        <Mail size={14} className="me-1" />
                        {caseDetails.assignedAgent.id.email || "N/A"}
                      </p>
                      <p className="text-muted mb-0">
                        <Phone size={14} className="me-1" />
                        {caseDetails.assignedAgent.id.mobileNo || "N/A"}
                      </p>
                    </div>
                  </div>

                  <ListGroup variant="flush" className="mb-3">
                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">Status</span>
                      <Badge bg="info">
                        {caseDetails.assignedAgent.status || "N/A"}
                      </Badge>
                    </ListGroup.Item>
                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">Assigned At</span>
                      <span>
                        {formatDate(caseDetails.assignedAgent.assignedAt)}
                      </span>
                    </ListGroup.Item>
                    {caseDetails.assignedAgent.acceptedAt && (
                      <ListGroup.Item className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">Accepted At</span>
                        <span>
                          {formatDate(caseDetails.assignedAgent.acceptedAt)}
                        </span>
                      </ListGroup.Item>
                    )}
                    {caseDetails.assignedAgent.completedAt && (
                      <ListGroup.Item className="d-flex justify-content-between align-items-center">
                        <span className="text-muted">Completed At</span>
                        <span>
                          {formatDate(caseDetails.assignedAgent.completedAt)}
                        </span>
                      </ListGroup.Item>
                    )}
                  </ListGroup>

                  {caseDetails.assignedAgent.observation && (
                    <div>
                      <h6 className="mb-2">Observation</h6>
                      <p className="text-muted">
                        {caseDetails.assignedAgent.observation}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-4">
                  <div className="icon-shape icon-lg bg-light-secondary text-secondary rounded-2 mb-3 mx-auto">
                    <User size={24} />
                  </div>
                  <p className="text-muted mb-0">No agent assigned</p>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Quick Stats */}
          <Card className="mb-4">
            <Card.Header>
              <h6 className="mb-0">Quick Stats</h6>
            </Card.Header>
            <Card.Body>
              <Row className="text-center">
                <Col xs={6} className="mb-3">
                  <div className="icon-shape icon-md bg-light-primary text-primary rounded-2 mb-2 mx-auto">
                    <Camera size={20} />
                  </div>
                  <h5 className="mb-1">
                    {Object.keys(caseDetails.images || {}).length}
                  </h5>
                  <small className="text-muted">Images</small>
                </Col>
                <Col xs={6} className="mb-3">
                  <div className="icon-shape icon-md bg-light-success text-success rounded-2 mb-2 mx-auto">
                    <CheckCircle size={20} />
                  </div>
                  <h5 className="mb-1">
                    {
                      Object.values(caseDetails.accessories || {}).filter(
                        (v) => v,
                      ).length
                    }
                  </h5>
                  <small className="text-muted">Accessories</small>
                </Col>
                <Col xs={6}>
                  <div className="icon-shape icon-md bg-light-warning text-warning rounded-2 mb-2 mx-auto">
                    <FileText size={20} />
                  </div>
                  <h5 className="mb-1">
                    {caseDetails.inspections?.length || 0}
                  </h5>
                  <small className="text-muted">Inspections</small>
                </Col>
                <Col xs={6}>
                  <div className="icon-shape icon-md bg-light-info text-info rounded-2 mb-2 mx-auto">
                    <FaWrench size={20} />
                  </div>
                  <h5 className="mb-1">
                    {caseDetails.itemsToRepair?.length || 0}
                  </h5>
                  <small className="text-muted">Repairs</small>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  const renderVehicleDetails = () => {
    if (!caseDetails) return null;

    const vehicleEdetails = caseDetails.vehicleEdetails || {};
    const otherDetails = caseDetails.otherDetails || {};

    return (
      <Row>
        {/* Vehicle E-Details */}
        <Col lg={6}>
          <Card className="mb-4">
            <Card.Header>
              <h6 className="mb-0">Vehicle E-Details</h6>
            </Card.Header>
            <Card.Body>
              <Accordion defaultActiveKey="0">
                <Accordion.Item eventKey="0">
                  <Accordion.Header>Registration Details</Accordion.Header>
                  <Accordion.Body>
                    <Table borderless size="sm">
                      <tbody>
                        <tr>
                          <td className="text-muted" width="40%">
                            Registration Authority
                          </td>
                          <td>{vehicleEdetails.regAuthority || "N/A"}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Registration Date</td>
                          <td>{vehicleEdetails.regDate || "N/A"}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">RC Expiry Date</td>
                          <td>{vehicleEdetails.rcExpiryDate || "N/A"}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Status</td>
                          <td>{vehicleEdetails.status || "N/A"}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Vehicle Class</td>
                          <td>{vehicleEdetails.vehicleClass || "N/A"}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="1">
                  <Accordion.Header>Ownership Details</Accordion.Header>
                  <Accordion.Body>
                    <Table borderless size="sm">
                      <tbody>
                        <tr>
                          <td className="text-muted" width="40%">
                            Owner
                          </td>
                          <td>{vehicleEdetails.owner || "N/A"}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Owner Count</td>
                          <td>{vehicleEdetails.ownerCount || "N/A"}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Father's Name</td>
                          <td>{vehicleEdetails.ownerFatherName || "N/A"}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Permanent Address</td>
                          <td>{vehicleEdetails.permanentAddress || "N/A"}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Present Address</td>
                          <td>{vehicleEdetails.presentAddress || "N/A"}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="2">
                  <Accordion.Header>Technical Specifications</Accordion.Header>
                  <Accordion.Body>
                    <Table borderless size="sm">
                      <tbody>
                        <tr>
                          <td className="text-muted" width="40%">
                            Body Type
                          </td>
                          <td>{vehicleEdetails.bodyType || "N/A"}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Vehicle Colour</td>
                          <td>{vehicleEdetails.vehicleColour || "N/A"}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Fuel Type</td>
                          <td>{vehicleEdetails.type || "N/A"}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Cubic Capacity</td>
                          <td>
                            {vehicleEdetails.vehicleCubicCapacity || "N/A"}
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted">Seating Capacity</td>
                          <td>
                            {vehicleEdetails.vehicleSeatCapacity || "N/A"}
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted">Unladen Weight</td>
                          <td>{vehicleEdetails.unladenWeight || "N/A"}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Gross Vehicle Weight</td>
                          <td>{vehicleEdetails.grossVehicleWeight || "N/A"}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </Card.Body>
          </Card>
        </Col>

        {/* Other Vehicle Details */}
        <Col lg={6}>
          <Card className="mb-4">
            <Card.Header>
              <h6 className="mb-0">Other Vehicle Details</h6>
            </Card.Header>
            <Card.Body>
              <Table borderless>
                <tbody>
                  <tr>
                    <td className="text-muted" width="40%">
                      Loan Agreement No
                    </td>
                    <td>{caseDetails.loanAgreementNo || "N/A"}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Document Verification</td>
                    <td>{caseDetails.documentVerification || "N/A"}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Manufacturing Date</td>
                    <td>{caseDetails.manufacturingDate || "N/A"}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Fuel Type</td>
                    <td>{caseDetails.fueltype || "N/A"}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Meter Reading</td>
                    <td>{caseDetails.meterReading || "N/A"}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Vehicle Color</td>
                    <td>{caseDetails.vehicleColor || "N/A"}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Hypothecation Bank</td>
                    <td>{caseDetails.hypothecationBank || "N/A"}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Insurance Company</td>
                    <td>{caseDetails.insuranceCompany || "N/A"}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Insurance Status</td>
                    <td>{caseDetails.insuranceStatus || "N/A"}</td>
                  </tr>
                </tbody>
              </Table>

              {/* Other Details Section */}
              <h6 className="mt-4 mb-3">Condition Details</h6>
              <Table borderless size="sm">
                <tbody>
                  <tr>
                    <td className="text-muted" width="40%">
                      Accidental Status
                    </td>
                    <td>{otherDetails.accidental || "N/A"}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Engine Condition</td>
                    <td>{otherDetails.engineCondition || "N/A"}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Interior Condition</td>
                    <td>{otherDetails.interiorCondition || "N/A"}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Exterior Condition</td>
                    <td>{otherDetails.exteriorCondition || "N/A"}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Level of Maintenance</td>
                    <td>{otherDetails.levelOfMaintenance || "N/A"}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Fitness Expiry</td>
                    <td>{otherDetails.fitnessExpiry || "N/A"}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Road Tax Validity</td>
                    <td>{otherDetails.roadTaxValidity || "N/A"}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Insurance Expiry</td>
                    <td>{otherDetails.insuranceExpiry || "N/A"}</td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  const renderImages = () => {
    if (!caseDetails?.images) return null;

    const images = caseDetails.images;
    const imageEntries = Object.entries(images).filter(
      ([key, value]) =>
        value && typeof value === "string" && value.trim() !== "",
    );

    if (imageEntries.length === 0) {
      return (
        <div className="text-center py-5">
          <div className="icon-shape icon-lg bg-light-secondary text-secondary rounded-2 mb-3 mx-auto">
            <Camera size={24} />
          </div>
          <h5 className="mb-2">No Images Available</h5>
          <p className="text-muted">
            No images have been uploaded for this case.
          </p>
        </div>
      );
    }

    return (
      <Row>
        {imageEntries.map(([key, value]) => (
          <Col key={key} xs={12} sm={6} md={4} lg={3} className="mb-4">
            <Card className="h-100">
              <Card.Body className="p-3">
                <div
                  className="image-container rounded mb-3"
                  onClick={() => {
                    setSelectedImage(value);
                    setShowImageModal(true);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <Image
                    src={value}
                    alt={key}
                    fluid
                    className="rounded"
                    style={{
                      height: "150px",
                      objectFit: "cover",
                      width: "100%",
                    }}
                  />
                  <div className="image-overlay">
                    <div className="icon-shape icon-sm bg-white text-dark rounded-circle">
                      <Maximize2 size={16} />
                    </div>
                  </div>
                </div>
                <h6 className="mb-1">
                  {key.replace(/([A-Z])/g, " $1").trim()}
                </h6>
                {images[key]?.label && (
                  <small className="text-muted">{images[key].label}</small>
                )}
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  const renderAccessories = () => {
    if (!caseDetails?.accessories) return null;

    const accessories = caseDetails.accessories;
    const accessoryEntries = Object.entries(accessories);

    const availableAccessories = accessoryEntries.filter(
      ([key, value]) => value === true,
    );
    const unavailableAccessories = accessoryEntries.filter(
      ([key, value]) => value === false,
    );

    return (
      <Row>
        <Col lg={6}>
          <Card className="mb-4">
            <Card.Header className="bg-success bg-opacity-10 border-success">
              <h6 className="mb-0 text-success">Available Accessories</h6>
            </Card.Header>
            <Card.Body>
              {availableAccessories.length === 0 ? (
                <div className="text-center py-4">
                  <div className="icon-shape icon-lg bg-light-secondary text-secondary rounded-2 mb-3 mx-auto">
                    <Package size={24} />
                  </div>
                  <p className="text-muted mb-0">No accessories available</p>
                </div>
              ) : (
                <ListGroup variant="flush">
                  {availableAccessories.map(([key]) => (
                    <ListGroup.Item
                      key={key}
                      className="d-flex align-items-center"
                    >
                      <CheckCircle size={16} className="text-success me-2" />
                      <span>{key.replace(/([A-Z])/g, " $1").trim()}</span>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="mb-4">
            <Card.Header className="bg-danger bg-opacity-10 border-danger">
              <h6 className="mb-0 text-danger">Unavailable Accessories</h6>
            </Card.Header>
            <Card.Body>
              {unavailableAccessories.length === 0 ? (
                <div className="text-center py-4">
                  <div className="icon-shape icon-lg bg-light-secondary text-secondary rounded-2 mb-3 mx-auto">
                    <Package size={24} />
                  </div>
                  <p className="text-muted mb-0">
                    All accessories are available
                  </p>
                </div>
              ) : (
                <ListGroup variant="flush">
                  {unavailableAccessories.map(([key]) => (
                    <ListGroup.Item
                      key={key}
                      className="d-flex align-items-center"
                    >
                      <XCircle size={16} className="text-danger me-2" />
                      <span>{key.replace(/([A-Z])/g, " $1").trim()}</span>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  const renderCondition = () => {
    if (!caseDetails?.condition) return null;

    const condition = caseDetails.condition;

    const renderConditionSection = (sectionName, data) => {
      if (!data) return null;

      return (
        <Card className="mb-4">
          <Card.Header>
            <h6 className="mb-0">{sectionName}</h6>
          </Card.Header>
          <Card.Body>
            <Table borderless>
              <tbody>
                {Object.entries(data).map(([key, value]) => {
                  if (
                    value &&
                    typeof value === "object" &&
                    value.value !== undefined
                  ) {
                    return (
                      <tr key={key}>
                        <td className="text-muted" width="40%">
                          {key.replace(/([A-Z])/g, " $1").trim()}
                        </td>
                        <td>
                          <Badge bg="info">{value.value || "N/A"}</Badge>
                          {value.options && (
                            <small className="text-muted d-block mt-1">
                              Options: {value.options.join(", ")}
                            </small>
                          )}
                        </td>
                      </tr>
                    );
                  } else if (typeof value === "string" && value.trim() !== "") {
                    return (
                      <tr key={key}>
                        <td className="text-muted" width="40%">
                          {key.replace(/([A-Z])/g, " $1").trim()}
                        </td>
                        <td>{value}</td>
                      </tr>
                    );
                  } else if (typeof value === "number") {
                    return (
                      <tr key={key}>
                        <td className="text-muted" width="40%">
                          {key.replace(/([A-Z])/g, " $1").trim()}
                        </td>
                        <td>{value}</td>
                      </tr>
                    );
                  }
                  return null;
                })}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      );
    };

    return (
      <div>
        {Object.entries(condition).map(([sectionName, sectionData]) =>
          renderConditionSection(
            sectionName.replace(/([A-Z])/g, " $1").trim(),
            sectionData,
          ),
        )}
      </div>
    );
  };

  const renderDocuments = () => {
    const signNdoc = caseDetails?.signNdoc || {};

    return (
      <Row>
        <Col lg={6}>
          <Card className="mb-4">
            <Card.Header>
              <h6 className="mb-0">Signatures</h6>
            </Card.Header>
            <Card.Body>
              <ListGroup variant="flush">
                {Object.entries(signNdoc).map(([key, value]) => {
                  if (!value) return null;

                  const isImage =
                    typeof value === "string" && value.trim() !== "";
                  const label = key.replace(/([A-Z])/g, " $1").trim();

                  return (
                    <ListGroup.Item
                      key={key}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <span>{label}</span>
                      {isImage ? (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => {
                            setSelectedImage(value);
                            setShowImageModal(true);
                          }}
                        >
                          View
                        </Button>
                      ) : (
                        <Badge bg="secondary">Not Available</Badge>
                      )}
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="mb-4">
            <Card.Header>
              <h6 className="mb-0">Required Assets</h6>
            </Card.Header>
            <Card.Body>
              {caseDetails.requiredAssets?.length > 0 ? (
                <ListGroup variant="flush">
                  {caseDetails.requiredAssets.map((asset, index) => (
                    <ListGroup.Item
                      key={index}
                      className="d-flex align-items-center"
                    >
                      <div
                        className={`icon-shape icon-sm bg-light-${asset.value ? "success" : "danger"} text-${asset.value ? "success" : "danger"} rounded-2 me-3`}
                      >
                        {asset.value ? (
                          <CheckCircle size={16} />
                        ) : (
                          <XCircle size={16} />
                        )}
                      </div>
                      <div>
                        <h6 className="mb-0">{asset.name}</h6>
                        <small className="text-muted">{asset.key}</small>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted">No required assets specified</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  };

  const renderInspections = () => {
    const inspections = caseDetails?.inspections || [];

    return (
      <div>
        {inspections.length === 0 ? (
          <div className="text-center py-5">
            <div className="icon-shape icon-lg bg-light-secondary text-secondary rounded-2 mb-3 mx-auto">
              <FileText size={24} />
            </div>
            <h5 className="mb-2">No Inspections</h5>
            <p className="text-muted">
              No inspections have been recorded for this case.
            </p>
          </div>
        ) : (
          inspections.map((inspection, index) => (
            <Card key={index} className="mb-4">
              <Card.Header>
                <Row className="align-items-center">
                  <Col>
                    <h6 className="mb-0">Inspection #{index + 1}</h6>
                  </Col>
                  <Col xs="auto">
                    <Badge
                      bg={
                        inspection.status === "completed"
                          ? "success"
                          : "warning"
                      }
                    >
                      {inspection.status}
                    </Badge>
                  </Col>
                </Row>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <h6 className="mb-3">Details</h6>
                    <ListGroup variant="flush">
                      <ListGroup.Item className="d-flex justify-content-between">
                        <span className="text-muted">Done By:</span>
                        <span>{inspection?.inspectionDoneBy?.name}</span>
                      </ListGroup.Item>
                      <ListGroup.Item className="d-flex justify-content-between">
                        <span className="text-muted">Phone No:</span>
                        <span>{inspection?.inspectionDoneBy?.phoneNo}</span>
                      </ListGroup.Item>
                      <ListGroup.Item className="d-flex justify-content-between">
                        <span className="text-muted">Date:</span>
                        <span>{formatDate(inspection.lastInspection)}</span>
                      </ListGroup.Item>
                      <ListGroup.Item className="d-flex justify-content-between">
                        <span className="text-muted">Status:</span>
                        <span>{inspection.status}</span>
                      </ListGroup.Item>
                    </ListGroup>
                  </Col>

                  <Col md={8}>
                    <h6 className="mb-3">Observations</h6>
                    <p className="text-muted">
                      {inspection.inspectionObservation ||
                        "No observations recorded"}
                    </p>

                    {inspection.notes && (
                      <>
                        <h6 className="mb-3 mt-4">Notes</h6>
                        <p className="text-muted">{inspection.notes}</p>
                      </>
                    )}

                    {inspection.images?.length > 0 && (
                      <>
                        <h6 className="mb-3 mt-4">
                          Images ({inspection.images.length})
                        </h6>
                        <Row>
                          {inspection.images.map((img, imgIndex) => (
                            <Col key={imgIndex} xs={4} sm={3} className="mb-3">
                              <Image
                                src={img}
                                alt={`Inspection ${index + 1} - Image ${imgIndex + 1}`}
                                fluid
                                className="rounded"
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                  setSelectedImage(img);
                                  setShowImageModal(true);
                                }}
                              />
                            </Col>
                          ))}
                        </Row>
                      </>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          ))
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Container fluid className="py-5">
        <Row className="justify-content-center">
          <Col md={6} className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading case details...</p>
          </Col>
        </Row>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="py-5">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Case Details</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={() => navigate("/cases")}>
            Back to Cases
          </Button>
        </Alert>
      </Container>
    );
  }

  if (!caseDetails) {
    return (
      <Container fluid className="py-5">
        <Alert variant="warning">
          <Alert.Heading>Case Not Found</Alert.Heading>
          <p>The requested case could not be found.</p>
          <Button variant="outline-warning" onClick={() => navigate("/cases")}>
            Back to Cases
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <Button
            variant="outline-primary"
            onClick={() => navigate("/cases")}
            className="mb-3"
          >
            <ArrowLeft size={18} className="me-2" />
            Back to Cases
          </Button>
          <div className="d-flex align-items-center">
            <div className="icon-shape icon-lg bg-light-primary text-primary rounded-2 me-3">
              <FileText size={24} />
            </div>
            <div>
              <h4 className="mb-0">Case Details</h4>
              <p className="text-muted mb-0">
                {caseDetails.caseID} •{" "}
                {vehicleTypeLabels[caseDetails.vehicleType]}
              </p>
            </div>
            <div className="ms-auto">{getStatusBadge()}</div>
          </div>
        </Col>
      </Row>

      {/* Tabs */}
      <Card className="mb-4">
        <Card.Header className="border-bottom-0 bg-white">
          <Nav variant="tabs" activeKey={activeTab} onSelect={setActiveTab}>
            <Nav.Item>
              <Nav.Link eventKey="overview">
                <FileText size={18} className="me-2" />
                Overview
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="vehicle">
                <FaCar size={18} className="me-2" />
                Vehicle Details
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="images">
                <Camera size={18} className="me-2" />
                Images
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="accessories">
                <Package size={18} className="me-2" />
                Accessories
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="condition">
                <Settings size={18} className="me-2" />
                Condition
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="documents">
                <FileText size={18} className="me-2" />
                Documents
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="inspections">
                <FileText size={18} className="me-2" />
                Inspections
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </Card.Header>

        <Card.Body>
          <Tab.Content>
            <Tab.Pane active={activeTab === "overview"}>
              {renderOverview()}
            </Tab.Pane>
            <Tab.Pane active={activeTab === "vehicle"}>
              {renderVehicleDetails()}
            </Tab.Pane>
            <Tab.Pane active={activeTab === "images"}>
              {renderImages()}
            </Tab.Pane>
            <Tab.Pane active={activeTab === "accessories"}>
              {renderAccessories()}
            </Tab.Pane>
            <Tab.Pane active={activeTab === "condition"}>
              {renderCondition()}
            </Tab.Pane>
            <Tab.Pane active={activeTab === "documents"}>
              {renderDocuments()}
            </Tab.Pane>
            <Tab.Pane active={activeTab === "inspections"}>
              {renderInspections()}
            </Tab.Pane>
          </Tab.Content>
        </Card.Body>
      </Card>

      {/* Action Buttons */}
      <Row className="mb-4">
        <Col className="text-end">
          <Button
            variant="outline-secondary"
            className="me-2"
            onClick={fetchCaseDetails}
          >
            <RefreshCw size={18} className="me-2" />
            Refresh
          </Button>
          <Button variant="primary" className="me-2">
            <FaEdit size={18} className="me-2" />
            Edit Case
          </Button>
          <Button variant="danger" onClick={() => deleteCase()}>
            <Trash2 size={18} className="me-2" />
            Delete Case
          </Button>
        </Col>
      </Row>

      {/* Image Modal */}
      <Modal
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Image Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {selectedImage && (
            <Image src={selectedImage} fluid className="rounded" />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImageModal(false)}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => window.open(selectedImage, "_blank")}
          >
            Open in New Tab
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CaseDetails;
